import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../../firebase/firebaseConfig';
import { collection, getDocs } from 'firebase/firestore';
import IconRenderer from '../ui/IconRenderer';
import CookbookSnippetProgressStats from '../ui/CookbookSnippetProgressStats';
import SpacedRepetition from '../SRS/SpacedRepetition';
import '../Page/Home.css';
import './GlobalSearch.css';
import { isRoadmapUserCookbook } from '../../services/roadmapService';
import {
  buildCardProgressMap,
  loadCookbookCardProgress,
  loadCookbookPracticeStartedCards
} from '../../utils/cookbookCardProgress';

/** Matches ~1.3× home row scale (IconRenderer `large` is 32px). */
const GLOBAL_SEARCH_LANG_ICON_ROW_STYLE = { width: '35px', height: '35px' };

function truncateTitle(title, maxLength = 85) {
  if (!title) return '';
  const s = String(title);
  if (s.length <= maxLength) return s;
  return s.substring(0, maxLength).trim() + '...';
}

/**
 * Search user cookbooks (decks) only. Legacy users/{uid}/snippets (SnippetManager) is not queried.
 */
const GlobalSearch = ({ userId, searchTerm, onStartStudying }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [practiceLoadingDeckId, setPracticeLoadingDeckId] = useState(null);
  const searchProgressMapRef = useRef(new Map());

  useEffect(() => {
    const performSearch = async () => {
      if (!searchTerm || searchTerm.trim().length < 2) {
        setSearchResults([]);
        return;
      }

      setLoading(true);
      try {
        const q = searchTerm.toLowerCase();
        const decksRef = collection(db, `users/${userId}/decks`);
        const srs = new SpacedRepetition(userId);
        const [decksSnapshot, progressRows] = await Promise.all([
          getDocs(decksRef),
          srs.fetchAllCardProgressRows()
        ]);
        const progressMap = buildCardProgressMap(progressRows);
        searchProgressMapRef.current = progressMap;

        const allDecks = decksSnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter((deck) => !deck.type || deck.type === 'cookbook');

        const filteredDecks = allDecks.filter(
          (deck) =>
            !isRoadmapUserCookbook(deck) &&
            (deck.name?.toLowerCase().includes(q) || deck.description?.toLowerCase().includes(q))
        );

        filteredDecks.sort((a, b) => {
          const aName = a.name || '';
          const bName = b.name || '';
          const aExact = aName.toLowerCase() === q;
          const bExact = bName.toLowerCase() === q;
          if (aExact && !bExact) return -1;
          if (!aExact && bExact) return 1;
          const aStarts = aName.toLowerCase().startsWith(q);
          const bStarts = bName.toLowerCase().startsWith(q);
          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;
          return 0;
        });

        const withProgress = await Promise.all(
          filteredDecks.map(async (deck) => {
            const progress = await loadCookbookCardProgress(userId, deck.id, progressMap);
            return {
              ...deck,
              cardCount: progress.total,
              snippetsStarted: progress.started,
              snippetsStartedNotDue: progress.startedNotDue,
              snippetsMastered: progress.mastered
            };
          })
        );

        setSearchResults(withProgress);
      } catch (error) {
        console.error('Error searching:', error);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [userId, searchTerm]);

  const handleCookbookActivate = (deck) => {
    if (onStartStudying) {
      onStartStudying({ type: 'deck', id: deck.id, deckId: deck.id, ...deck });
    }
  };

  const handlePracticeStartedCookbook = useCallback(
    async (e, item) => {
      e.stopPropagation();
      const deckId = item?.id;
      if (!deckId || !userId || !onStartStudying || practiceLoadingDeckId) return;
      if (!item.snippetsStarted) return;

      setPracticeLoadingDeckId(deckId);
      try {
        const progressMap =
          searchProgressMapRef.current instanceof Map && searchProgressMapRef.current.size > 0
            ? searchProgressMapRef.current
            : buildCardProgressMap(await new SpacedRepetition(userId).fetchAllCardProgressRows());
        const cards = await loadCookbookPracticeStartedCards(userId, deckId, progressMap);
        if (cards.length === 0) return;

        onStartStudying({
          practiceStartedSession: true,
          deckId,
          cards,
          startCardId: cards[0].id
        });
      } catch (err) {
        console.error('GlobalSearch: failed to start cookbook practice session', err);
      } finally {
        setPracticeLoadingDeckId(null);
      }
    },
    [userId, onStartStudying, practiceLoadingDeckId]
  );

  if (loading) {
    return <div className="global-search-loading">Searching...</div>;
  }

  if (!searchTerm || searchTerm.trim().length < 2) {
    return null;
  }

  if (searchResults.length === 0) {
    return <div className="global-search-no-results">No results found for "{searchTerm}"</div>;
  }

  return (
    <div className="global-search-results">
      <h2 className="section-title">Search Results for "{searchTerm}"</h2>
      <div className="global-search-count">{searchResults.length} results found</div>

      <div className="global-search-results__cookbook-grid">
        {searchResults.map((item) => (
          <div
            key={item.id}
            className="deck-item recent-deck home-in-progress__card home-in-progress__card--roadmap-row home-in-progress__card--cookbook global-search-results__cookbook-card"
            role="button"
            tabIndex={0}
            onClick={() => handleCookbookActivate(item)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCookbookActivate(item);
              }
            }}
          >
            <div className="home-in-progress__roadmap-row-main">
              <div className="home-in-progress__row-leading">
                <IconRenderer
                  name={item.language && String(item.language).trim() ? item.language.toLowerCase() : 'code'}
                  type="language"
                  size="large"
                  className="home-in-progress__row-language-icon"
                  style={GLOBAL_SEARCH_LANG_ICON_ROW_STYLE}
                />
                <div className="home-in-progress__cookbook-title-block">
                  <h3 className="home-in-progress__row-title">{truncateTitle(item.name)}</h3>
                  <CookbookSnippetProgressStats
                    className="home-in-progress__cookbook-progress"
                    started={item.snippetsStarted}
                    mastered={item.snippetsMastered}
                    total={item.cardCount}
                    cookbookTitle={item.name}
                    onStartedClick={(e) => handlePracticeStartedCookbook(e, item)}
                    startedDisabled={
                      practiceLoadingDeckId === item.id ||
                      !item.snippetsStarted
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GlobalSearch;
